import { useMemo } from 'react';
import { Sparkles, CheckCircle2 } from 'lucide-react';

/**
 * Lightweight token overlap between a suggested question and what the rep
 * said. We strip stopwords, normalize, and compute a Jaccard-style overlap
 * of the remaining content tokens. >= 0.4 means the rep almost certainly
 * asked it; we mark such suggestions as "used".
 */
const STOPWORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'do', 'does',
  'for', 'from', 'has', 'have', 'how', 'i', 'if', 'in', 'is', 'it',
  'its', 'of', 'on', 'or', 'so', 'that', 'the', 'their', 'them',
  'they', 'this', 'to', 'was', 'we', 'were', 'what', 'when', 'where',
  'which', 'who', 'why', 'will', 'with', 'would', 'you', 'your',
  'about', 'any', 'can', 'could', 'd', 's', 't', 're', 've', 'll',
  "you're", "we're", "i'm", "it's", "don't",
]);

function tokenize(text) {
  if (!text) return new Set();
  return new Set(
    text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3 && !STOPWORDS.has(w))
  );
}

function overlapScore(qTokens, repTokens) {
  if (qTokens.size === 0) return 0;
  let hit = 0;
  qTokens.forEach(t => { if (repTokens.has(t)) hit += 1; });
  return hit / qTokens.size;
}

/**
 * Renders a horizontal scrollable strip of recent AI-suggested questions
 * the rep actually used. Detection: token overlap between each suggested
 * question and the rep's transcript >= 0.4.
 *
 * Props:
 *   suggestedQuestions: latest analysis questions
 *   transcript: full live transcript array
 *   maxHighlights: optional cap (defaults to 3)
 */
export function CoachingHighlights({ suggestedQuestions, transcript, maxHighlights = 3 }) {
  const highlights = useMemo(() => {
    if (!Array.isArray(suggestedQuestions) || suggestedQuestions.length === 0) return [];
    if (!Array.isArray(transcript) || transcript.length === 0) return [];

    // Concatenate only the rep's lines (we don't want a "match" when the
    // PROSPECT happens to say the question's keywords).
    const repText = transcript
      .filter(t => t.speaker === 'rep')
      .map(t => t.text || '')
      .join(' ');
    if (!repText.trim()) return [];

    const repTokens = tokenize(repText);
    const used = [];
    suggestedQuestions.forEach((q, idx) => {
      const text = q.question || q.text || '';
      if (!text) return;
      const qTokens = tokenize(text);
      const score = overlapScore(qTokens, repTokens);
      if (score >= 0.4) {
        used.push({ ...q, _score: score, _idx: idx });
      }
    });
    // Highest-overlap first, capped at maxHighlights
    used.sort((a, b) => b._score - a._score);
    return used.slice(0, maxHighlights);
  }, [suggestedQuestions, transcript, maxHighlights]);

  if (highlights.length === 0) return null;

  return (
    <div
      className="border-t border-emerald-100 bg-gradient-to-r from-emerald-50/60 via-white to-emerald-50/40 px-4 py-2"
      data-testid="coaching-highlights"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
          Coaching highlights · questions you used
        </span>
        <span className="text-[10px] text-emerald-600/70 font-medium">
          ({highlights.length})
        </span>
      </div>
      <div
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        style={{ scrollbarWidth: 'thin' }}
      >
        {highlights.map((h, i) => (
          <div
            key={`${h._idx}-${i}`}
            className="flex-shrink-0 max-w-xs bg-white border border-emerald-200 rounded-md px-2.5 py-1.5 shadow-sm"
            data-testid={`coaching-highlight-${i}`}
          >
            <div className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                {h.category && (
                  <div className="text-[9px] font-semibold uppercase tracking-wide text-emerald-700/70 mb-0.5">
                    {h.category}
                  </div>
                )}
                <p className="text-xs text-zinc-800 leading-snug line-clamp-2">
                  {h.question || h.text}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
